'use client';

import {
  AlertTriangle,
  CheckIcon,
  ChevronDown,
  CirclePlus,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/command';
import { useElementWidth } from '@/hooks';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/popover';
import { InfoIcon } from '@/components/InfoIcon';
import { TruncatedTooltip } from '@/components/TruncatedTooltip';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { wordsFilter } from '@/utility/textSearch';
import styles from './ComboBox.module.css';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type UIEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';

export interface ComboBoxOption<T extends string | number = string> {
  value: T;
  label: string;
  description?: string;
  disabled?: boolean;
  visibility?: boolean;
  tooltip?: string;
  /** More words the search matches (the label in the other language). */
  keywords?: string[];
  /** Key of the `groups` section this option is listed under. */
  group?: string;
}

/** One section of a grouped list: its key and its heading. */
export interface ComboBoxGroup {
  key: string;
  label: string;
}

export type MultiSelectOnChangeProps<T extends string | number = string> =
  | {
      value?: T[];
      multiple: true | '';
      onChange?: (value: T[]) => void;
      withSelectAll?: boolean;
    }
  | {
      value?: T;
      multiple?: false;
      onChange?: (value?: T) => void;
      withSelectAll?: never;
    };

/** State handed to `renderOption` so a custom row can reflect selection. */
export interface ComboBoxOptionState {
  selected: boolean;
  disabled: boolean;
}

export type ComboBoxProps<T extends string | number = string> =
  MultiSelectOnChangeProps<T> & {
    options: ComboBoxOption<T>[];
    /**
     * `md` (default) is the original control. `sm` keeps the trigger at 2.5rem
     * on every breakpoint instead of growing to 3rem from `sm` up.
     */
    size?: 'sm' | 'md';
    /**
     * Replaces the row body (label, description and tick) with your own node.
     * The row stays a real option — search, keyboard nav and selection are
     * unchanged — so render only the contents, not the wrapper.
     */
    renderOption?: (
      option: ComboBoxOption<T>,
      state: ComboBoxOptionState
    ) => React.ReactNode;
    /** Icon rendered at the trigger's leading edge. */
    startAdornment?: React.ReactNode;
    onSearch?: (value: string) => void;
    /**
     * Text already in the search box each time the list opens (also sent to
     * `onSearch`), e.g. a name typed elsewhere in the form.
     */
    initialSearch?: string;
    /**
     * `fuzzy` (default): cmdk's own scoring. `words`: every typed word must
     * appear in the label, description or `keywords`, accents and case
     * ignored ("code postal", "telephone urgence").
     */
    searchMode?: 'fuzzy' | 'words';
    /**
     * Lists the options under headings, one section per group in this order
     * (an option names its section with `group`). Empty sections are hidden,
     * also while searching.
     */
    groups?: ComboBoxGroup[];
    placeholder?: string;
    searchPlaceholder?: string;
    emptyMessage?: string;
    noEntriesMessage?: string;
    selectAllLabel?: string;
    addNewLabel?: string;
    /** Where the add-new action sits: under the list (default) or above it. */
    addNewPosition?: 'top' | 'bottom';
    noneLabel?: string;
    width?: string;
    error?: string;
    className?: string;
    disabled?: boolean;
    displaySearchBar?: boolean;
    none?: boolean;
    allowUnselect?: boolean;
    onOpenChange?: (open: boolean) => void;
    onLoadMore?: () => void;
    hasMore?: boolean;
    isLoadingMore?: boolean;
    popoverClassName?: string;
  };

export function ComboBox<T extends string | number>({
  options,
  value = [],
  onChange,
  onSearch,
  initialSearch,
  disabled,
  className = '',
  placeholder,
  searchPlaceholder,
  emptyMessage,
  noEntriesMessage,
  selectAllLabel,
  addNewLabel,
  addNewPosition = 'bottom',
  noneLabel,
  width,
  error,
  onAddNew,
  multiple = false,
  withSelectAll = false,
  displaySearchBar = true,
  none = false,
  allowUnselect = false,
  onOpenChange,
  onLoadMore,
  hasMore,
  isLoadingMore,
  popoverClassName,
  size = 'md',
  renderOption,
  startAdornment,
  searchMode = 'fuzzy',
  groups,
}: ComboBoxProps<T> & { onAddNew?: () => void }) {
  const { t } = useTranslation(TranslationNS.Filters);
  const finalPlaceholder = placeholder ?? t('combobox.placeholder');
  const finalSearchPlaceholder =
    searchPlaceholder ?? t('combobox.search_placeholder');
  const finalEmptyMessage = emptyMessage ?? t('combobox.empty_message');
  const finalNoEntriesMessage = noEntriesMessage ?? t('combobox.empty_state');
  const finalSelectAllLabel = selectAllLabel ?? t('combobox.select_all');
  const finalAddNewLabel = addNewLabel ?? t('combobox.add_new');
  const finalNoneLabel = noneLabel ?? t('combobox.none');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [triggerHeight, setTriggerHeight] = useState(0);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;
  const pendingLoadRef = useRef(false);
  multiple = multiple === '' || multiple;

  const handleScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      if (!onLoadMoreRef.current || !hasMore || isLoadingMore) return;
      const el = e.currentTarget;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 50) {
        onLoadMoreRef.current();
      }
    },
    [hasMore, isLoadingMore]
  );

  // Reset the pending flag whenever new options arrive so the next check can fire.
  useEffect(() => {
    pendingLoadRef.current = false;
  }, [options]);

  // When the list isn't tall enough to scroll, onScroll never fires.
  // Check after each render whether the container is non-scrollable and load more.
  // pendingLoadRef prevents double-firing during the window between isFetching
  // flipping false and the next setPage causing it to flip back to true.
  useEffect(() => {
    if (
      !open ||
      !onLoadMoreRef.current ||
      !hasMore ||
      isLoadingMore ||
      pendingLoadRef.current
    )
      return;
    const rafId = requestAnimationFrame(() => {
      const list = sentinelRef.current?.closest(
        '[data-slot="command-list"]'
      ) as HTMLElement | null;
      if (list && list.scrollHeight <= list.clientHeight) {
        pendingLoadRef.current = true;
        onLoadMoreRef.current?.();
      }
    });
    return () => cancelAnimationFrame(rafId);
  }, [open, options, isLoadingMore, hasMore]);

  const { ref: parentRef, width: parentWidth } =
    useElementWidth<HTMLButtonElement>();
  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setTriggerHeight(parentRef.current?.offsetHeight ?? 0);
      setQuery(initialSearch ?? '');
      if (initialSearch) onSearch?.(initialSearch);
    }
    setOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  const noneOption: ComboBoxOption<T> = {
    label: finalNoneLabel,
    value: '' as T,
    visibility: true,
    disabled: false,
    tooltip: undefined,
  };

  const finalOptions = useMemo<ComboBoxOption<T>[]>(
    () => [...(none && options.length > 0 ? [noneOption] : []), ...options],
    // noneOption is stable by value; options and none are the real deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [none, options]
  );

  if (!Array.isArray(value)) {
    value = value !== undefined && value !== null ? [value] : [];
  }

  // "Select All" only ever targets the real options — the synthetic "None"
  // entry (added above when `none` is set) is a clear-selection shortcut, not
  // a selectable value, so it must never be counted here or added to value.
  const isAllSelected = useMemo(() => {
    return multiple && options.length > 0 && value.length === options.length;
  }, [multiple, options, value]);

  let label = finalPlaceholder;
  if (multiple) {
    if (isAllSelected && withSelectAll) {
      label = finalSelectAllLabel;
    } else if (value.length > 0) {
      const selectedOptions = finalOptions.filter((opt) =>
        value.includes(opt.value)
      );
      label = selectedOptions.map((opt) => opt.label).join(', ');
    }
  } else {
    const selectedOption = finalOptions.find((opt) => opt.value === value[0]);
    if (selectedOption) label = selectedOption.label;
  }

  const finalWidth =
    width ?? (parentWidth ? `${Math.round(parentWidth)}px` : '200px');

  const onItemSelect = (currentValue: T) => {
    if (none && currentValue === ('' as T)) {
      onChange?.((multiple ? [] : undefined) as never);
      handleOpenChange(false);
      return;
    }
    if (!multiple) {
      const isSame = value[0] === currentValue;
      onChange?.((isSame && allowUnselect ? undefined : currentValue) as never);
      handleOpenChange(false);
      return;
    }
    if (value.includes(currentValue)) {
      onChange?.(value.filter((v) => v !== currentValue) as never);
      return;
    }
    onChange?.([...value, currentValue] as never);
  };

  const visibleOptions = finalOptions.filter(
    (opt) => opt?.visibility !== false
  );
  // With `groups`, each group is its own section under a heading, in the
  // order given; a group with no option (or none matching the search) is
  // hidden. Options outside every group stay in the plain list above them.
  const hasGroups = !!groups?.length;
  const groupKeys = new Set(groups?.map((group) => group.key));
  const ungroupedOptions = hasGroups
    ? visibleOptions.filter((opt) => !opt.group || !groupKeys.has(opt.group))
    : visibleOptions;
  const renderItem = (opt: ComboBoxOption<T>) => {
    const selected = value.includes(opt.value);
    return (
      <CommandItem
        key={opt.value}
        value={opt.value.toString()}
        keywords={[
          opt.label,
          ...(opt.description ? [opt.description] : []),
          ...(opt.keywords ?? []),
        ]}
        // Skipped by the keyboard too, not only the mouse.
        disabled={opt.disabled}
        onSelect={() => onItemSelect(opt.value)}
        className={cn(
          styles.comboboxItemRoot,
          size === 'sm' && styles.comboboxItemRootSm,
          selected && styles.comboboxItemActive,
          opt.disabled && styles.comboboxItemDisabled
        )}
      >
        {renderOption ? (
          renderOption(opt, {
            selected,
            disabled: !!opt.disabled,
          })
        ) : (
          <div className={styles.comboboxItem}>
            <div className={styles.comboboxItemText}>
              <TruncatedTooltip
                text={opt.label}
                lines={1}
                className={styles.comboboxItemLabel}
              />
              {opt.description && (
                <span className={styles.comboboxItemDescription}>
                  {opt.description}
                </span>
              )}
            </div>
            <CheckIcon
              className={cn(
                selected
                  ? styles.comboboxCheckVisible
                  : styles.comboboxCheckHidden
              )}
            />
          </div>
        )}
        {opt.tooltip && <InfoIcon size={14} content={opt.tooltip} />}
      </CommandItem>
    );
  };

  const isPlaceholder = label === placeholder;

  const addNew = onAddNew && (
    // forceMount keeps the action visible whatever the search is (cmdk
    // would otherwise filter it out). Its value is the label, so a
    // label that echoes the search ("Create "x"") matches it and Enter
    // picks it; onSelect makes Enter act like a click.
    <CommandItem
      forceMount
      value={finalAddNewLabel}
      className={cn(
        styles.comboboxAddNewWrap,
        addNewPosition === 'top' && styles.comboboxAddNewWrapTop
      )}
      onSelect={() => {
        onAddNew();
        handleOpenChange(false);
      }}
    >
      <button
        type="button"
        onClick={(event) => {
          // The item's own onSelect also fires on a click: stop
          // it, or "Add new" opens its dialog twice.
          event.stopPropagation();
          onAddNew();
          handleOpenChange(false);
        }}
        className={styles.comboboxAddNewBtn}
      >
        <span className={styles.comboboxAddNewIcon}>
          <CirclePlus />
        </span>
        {finalAddNewLabel}
      </button>
    </CommandItem>
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={true}>
      <PopoverTrigger asChild>
        <button
          ref={parentRef}
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            styles.comboboxTrigger,
            size === 'sm' && styles.comboboxTriggerSm,
            disabled && styles.comboboxDisabled,
            error && styles.comboboxError,
            className
          )}
        >
          {startAdornment && (
            <span className={styles.comboboxStartAdornment} aria-hidden="true">
              {startAdornment}
            </span>
          )}

          {isPlaceholder ? (
            <span className={cn(styles.comboboxPlaceholder, 'truncate')}>
              {label}
            </span>
          ) : multiple && value.length > 0 ? (
            <span className={styles.comboboxChipsWrap}>
              {finalOptions
                .filter((opt) => value.includes(opt.value))
                .map((opt) => (
                  <span key={opt.value} className={styles.comboboxChip}>
                    {opt.label}
                    <span
                      role="button"
                      tabIndex={0}
                      className={styles.comboboxChipRemove}
                      onClick={(e) => {
                        e.stopPropagation();
                        onChange?.(
                          value.filter((v) => v !== opt.value) as never
                        );
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.stopPropagation();
                          onChange?.(
                            value.filter((v) => v !== opt.value) as never
                          );
                        }
                      }}
                    >
                      ×
                    </span>
                  </span>
                ))}
            </span>
          ) : (
            <span className="truncate">{label}</span>
          )}

          {error ? (
            <AlertTriangle className={styles.comboboxIcon} />
          ) : (
            <ChevronDown className={styles.comboboxChevron} />
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={-triggerHeight}
        className={cn(styles.comboboxPopover, popoverClassName)}
        style={{ width: finalWidth }}
      >
        <Command
          className={styles.comboboxCommand}
          filter={searchMode === 'words' ? wordsFilter : undefined}
        >
          {displaySearchBar && (
            <CommandInput
              className={styles.comboboxSearch}
              placeholder={
                !multiple && label !== finalPlaceholder
                  ? label
                  : finalSearchPlaceholder
              }
              value={query}
              onValueChange={(val) => {
                setQuery(val);
                onSearch?.(val);
              }}
              onKeyDown={(event) => {
                // The add-new action sits outside the list cmdk navigates, so
                // Enter reaches it only when no option is highlighted (the
                // search matched nothing): then Enter means "create this".
                if (event.key !== 'Enter' || !onAddNew) return;
                const highlighted = event.currentTarget
                  .closest('[cmdk-root]')
                  ?.querySelector(
                    '[cmdk-list] [cmdk-item][data-selected="true"]'
                  );
                if (highlighted) return;
                event.preventDefault();
                onAddNew();
                handleOpenChange(false);
              }}
            />
          )}

          <div className={styles.comboboxBody}>
            <div className={styles.comboboxBodyInner}>
              {addNewPosition === 'top' && addNew}
              <CommandList onScroll={handleScroll}>
                {options.length === 0 ? (
                  <EmptyState message={finalNoEntriesMessage} />
                ) : (
                  <CommandEmpty>
                    <EmptyState message={finalEmptyMessage} />
                  </CommandEmpty>
                )}

                {options.length > 0 &&
                  (withSelectAll || ungroupedOptions.length > 0) && (
                    <CommandGroup>
                      {withSelectAll && (
                        <CommandItem
                          key="selectAll"
                          value="selectAll"
                          className={styles.comboboxItemRoot}
                          onSelect={() => {
                            const newValue = isAllSelected
                              ? []
                              : options.map((opt) => opt.value);
                            onChange?.(newValue as never);
                          }}
                        >
                          <div className={styles.comboboxItem}>
                            <CheckIcon
                              className={cn(
                                isAllSelected
                                  ? styles.comboboxCheckVisible
                                  : styles.comboboxCheckHidden
                              )}
                            />
                            <TruncatedTooltip
                              text={finalSelectAllLabel}
                              lines={1}
                              className={styles.comboboxItemLabel}
                            />
                          </div>
                        </CommandItem>
                      )}

                      {ungroupedOptions.map(renderItem)}
                      {!hasGroups && <div ref={sentinelRef} />}
                    </CommandGroup>
                  )}

                {options.length > 0 && hasGroups && (
                  <>
                    {groups!.map((group) => {
                      const items = visibleOptions.filter(
                        (opt) => opt.group === group.key
                      );
                      if (!items.length) return null;
                      return (
                        <CommandGroup key={group.key} heading={group.label}>
                          {items.map(renderItem)}
                        </CommandGroup>
                      );
                    })}
                    <div ref={sentinelRef} />
                  </>
                )}

                {isLoadingMore && (
                  <div className={styles.comboboxLoadingMore}>
                    <LoadingSpinner
                      size={16}
                      className="text-(--brand-muted)"
                    />
                  </div>
                )}
              </CommandList>
              {addNewPosition === 'bottom' && addNew}
            </div>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

interface EmptyStateProps {
  message?: string;
}

function EmptyState({ message }: EmptyStateProps) {
  const { t } = useTranslation(TranslationNS.Filters);
  const finalMessage = message ?? t('combobox.empty_state');
  return (
    <div className={styles.comboboxEmpty}>
      <p>{finalMessage}</p>
    </div>
  );
}
